import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LayoutDashboard, LogOut, User, ChevronDown, ShieldCheck } from "lucide-react";

const primary = [
  { to: "/", label: "Home", end: true },
  { to: "/plan", label: "Plan Your Business" },
  { to: "/marketplace", label: "Our Work" },
  { to: "/services", label: "Services" },
];
const veterans = { to: "/veterans", label: "For Veterans" };
const explore = [
  { to: "/start-your-business", label: "Start Your Business" },
  { to: "/solutions", label: "Solutions" },
  { to: "/business-plan", label: "Business Plan" },
  { to: "/ai-concierge", label: "AI Concierge" },
  { to: "/ai-chat-assistant", label: "AI Chat Assistant" },
  { to: "/launch-package", label: "Launch Package" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (exploreRef.current && !exploreRef.current.contains(e.target)) setExploreOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const dashHref = user && user.role === "admin" ? "/admin" : "/portal";

  return (
    <header className="fixed top-0 w-full z-50 bg-navy/80 backdrop-blur-xl border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <div className="w-10 h-10 border border-gold flex items-center justify-center font-heading font-bold text-gold text-lg tracking-tight group-hover:bg-gold group-hover:text-navy transition-colors">
            ATF
          </div>
          <div className="leading-none">
            <div className="font-heading text-white font-semibold tracking-tight">WebWorks</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Ask Top Foster</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {primary.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? "text-gold" : "text-gray-300 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink to={veterans.to} data-testid="nav-military-veterans"
            className={({ isActive }) => `flex items-center gap-1.5 text-sm tracking-wide transition-colors ${isActive ? "text-gold" : "text-gray-300 hover:text-white"}`}>
            <ShieldCheck size={14} /> {veterans.label}
          </NavLink>
          <div className="relative" ref={exploreRef}>
            <button onClick={() => setExploreOpen((v) => !v)} data-testid="nav-explore-toggle"
              className="flex items-center gap-1 text-sm tracking-wide text-gray-300 hover:text-white transition-colors">
              Explore <ChevronDown size={14} className={`transition-transform ${exploreOpen ? "rotate-180" : ""}`} />
            </button>
            {exploreOpen && (
              <div className="absolute top-full right-0 mt-3 w-56 bg-navy border border-white/10 shadow-2xl shadow-black/50 py-2" data-testid="nav-explore-menu">
                {explore.map((l) => (
                  <NavLink key={l.to} to={l.to} onClick={() => setExploreOpen(false)}
                    data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) => `block px-4 py-2.5 text-sm transition-colors ${isActive ? "text-gold" : "text-gray-300 hover:text-white hover:bg-white/5"}`}>
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <>
              <button
                onClick={() => navigate(dashHref)}
                data-testid="nav-dashboard-btn"
                className="flex items-center gap-2 text-sm text-gray-200 hover:text-gold transition-colors"
              >
                <LayoutDashboard size={16} /> {user.role === "admin" ? "Admin" : "Portal"}
              </button>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="nav-logout-btn"
                aria-label="Sign out"
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login-btn" className="text-sm text-gray-200 hover:text-gold transition-colors flex items-center gap-2">
              <User size={16} /> Sign In
            </Link>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-navy border-t border-white/10 px-6 py-6 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {[...primary, veterans, ...explore].map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
              data-testid={l.to === "/veterans" ? "nav-mobile-military-veterans" : undefined}
              className={l.to === "/veterans" ? "flex items-center gap-1.5 text-gray-200 hover:text-gold" : "text-gray-200 hover:text-gold"}>
              {l.to === "/veterans" ? <ShieldCheck size={15} /> : null}{l.label}
            </NavLink>
          ))}
          <div className="h-px bg-white/10 my-2" />
          {user ? (
            <>
              <button onClick={() => { setOpen(false); navigate(dashHref); }} className="text-left text-gray-200">Dashboard</button>
              <button onClick={async () => { setOpen(false); await logout(); navigate("/"); }} className="text-left text-gray-400">Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="text-gray-200">Sign In</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
